import { useDebouncedState } from '@mantine/hooks';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { TaskStatus, TaskTemplate } from '@ndvr/customer-api';

import { Page, PageContent, PageTopBar } from '@/components/ui/page-layout';
import { useHouseholdMap } from 'views/household/household-queries';
import TaskOverlays from 'views/tasks/TaskOverlays';

import { useDockState } from '../dock/dock-state';
import { useFilteredTaskViews } from './hooks/useFilteredTaskViews';
import { useSortedTaskViews } from './hooks/useSortedTaskViews';
import { useDueTaskViews } from './task-queries';
import { TaskLayout, useTaskOverlayState, useTaskViewState } from './task-state';
import { getDueDate } from './task-utils';
import { TaskFilterBar } from './widgets/TaskFilterBar';
import { TaskKanbanView } from './widgets/TaskKanbanView';
import { TaskListView } from './widgets/TaskListView';
import TemplateDialog from './widgets/TemplateDialog';

export const TasksPage = () => {
    const [isTemplateOpen, setIsTemplateOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useDebouncedState('', 500);
    const exportCsvRef = useRef<(() => void) | null>(null);
    const { taskViewState } = useTaskViewState();
    const householdMap = useHouseholdMap();
    const { addCreateTaskDockItem } = useDockState();
    const { taskGUID } = useParams<{ taskGUID: string }>();
    const { openViewTaskSheet } = useTaskOverlayState();

    useEffect(() => {
        if (taskGUID) {
            openViewTaskSheet(taskGUID);
        }
    }, [taskGUID]);

    const { data: rawTasks = [], isLoading } = useDueTaskViews(getDueDate(taskViewState));

    const filteredTasks = useFilteredTaskViews({
        tasks: rawTasks,
        selectedAssignees: taskViewState.selectedAssignees,
        selectedHouseholds: taskViewState.selectedHouseholds,
        selectedTaskTypes: taskViewState.selectedTaskTypes,
        selectedTaskStatuses: taskViewState.selectedTaskStatuses,
        searchTerm,
        householdMap,
    });

    const tasks = useSortedTaskViews({
        tasks: filteredTasks,
        taskSortOption: taskViewState.sortOption,
        sortDirection: taskViewState.sortDirection,
    });

    const handleUseTemplate = (tpl: TaskTemplate) => {
        setIsTemplateOpen(false);
        addCreateTaskDockItem(undefined, {
            title: tpl.title,
            description: tpl.description,
            assigneeOption: { value: tpl.defaultAssignee, label: '' },
            taskTypeOption: { value: tpl.type, label: '' },
            taskStatusOption: { value: TaskStatus.NEW, label: '' },
            dueDate: '',
            isRecurring: false,
        });
    };

    return (
        <Page>
            <PageTopBar />
            <TaskFilterBar
                taskCount={filteredTasks.length}
                rawTasks={rawTasks}
                householdMap={householdMap}
                onSearchChange={setSearchTerm}
                onNewTask={() => addCreateTaskDockItem()}
                onTemplateClick={() => setIsTemplateOpen(true)}
                onExportCsv={() => exportCsvRef.current?.()}
            />
            <PageContent scroll="children" padding="none">
                {taskViewState.layout === TaskLayout.LIST ? (
                    <TaskListView tasks={tasks} isLoading={isLoading} exportCsvRef={exportCsvRef} />
                ) : (
                    <div className="min-h-0 flex-1 px-6 pt-4 pb-6">
                        <TaskKanbanView tasks={tasks} isLoading={isLoading} />
                    </div>
                )}
            </PageContent>

            <TaskOverlays />
            <TemplateDialog
                isOpen={isTemplateOpen}
                onClose={() => setIsTemplateOpen(false)}
                onSelectTemplate={handleUseTemplate}
            />
        </Page>
    );
};
